import {
    getSelected,
    getSelectedPlacements,
    setSelected,
} from '@app/utils/filtersHelperFunctions';
import { parseEndDate, parseStartDate } from '@app/utils/parseDate';
import { BroadcastRsp, Rightsholder } from '@app/types/api';
import { isEmpty } from 'ramda';
import * as R from 'ramda';

enum BroadcastTab {
    VIEWERSHIP = 'viewership',
    EXPOSURES = 'exposures',
    VERBAL_MENTIONS = 'verbalMentions',
    DEMOGRAPHICS = 'demographics',
}

enum ExposureType {
    Visual = 'visual',
    VerbalMention = 'verbalMention',
}

type FilterPlacement = {
    id?: string | number;
    name?: string;
};

/**
 * League and team attribution modes use different RH-placement id pairs for
 * the same entity. Match by rightsholder + placement name across those lists.
 *
 * Per-RH placement names are not unique in the DB, so callers that resolve
 * ids (not just labels) must require a single match.
 */
const findFilterPlacementsByRhAndName = (
    placementName: string | undefined,
    rhName: string | undefined,
    placementsById: Record<string, FilterPlacement> = {},
    rightsholdersById: Record<string, Rightsholder> = {},
): FilterPlacement[] => {
    if (!placementName || !rhName) {
        return [];
    }

    return Object.values(placementsById).filter(filterPlacement => {
        if (!filterPlacement?.name || filterPlacement.name !== placementName) {
            return false;
        }

        const [placementRhId] = String(filterPlacement.id ?? '').split('-');
        if (!placementRhId) return false;

        return rightsholdersById[placementRhId]?.name === rhName;
    });
};

const resolvePlacementName = (
    broadcastRsp: BroadcastRsp | undefined,
    placementsById: Record<string, FilterPlacement> = {},
    rightsholdersById: Record<string, Rightsholder> = {},
) => {
    const { rightsholder, placement } = broadcastRsp ?? {};
    const { id: rhId, name: rhName } = rightsholder ?? {};
    const { id: plcId, name: apiPlacementName } = placement ?? {};

    const directMatch = placementsById[`${rhId}-${plcId}`]?.name;
    if (directMatch) return directMatch;

    const byNameMatches = findFilterPlacementsByRhAndName(
        apiPlacementName,
        rhName,
        placementsById,
        rightsholdersById,
    );

    // Any match shares the same display name; duplicates are safe for labels.
    if (byNameMatches[0]?.name) {
        return byNameMatches[0].name;
    }

    return apiPlacementName ?? 'Not found';
};

const resolveFilterPlacementId = (
    broadcastRsp: BroadcastRsp | undefined,
    placementsById: Record<string, FilterPlacement> = {},
    rightsholdersById: Record<string, Rightsholder> = {},
): string | null => {
    const { rightsholder, placement } = broadcastRsp ?? {};
    const { id: rhId, name: rhName } = rightsholder ?? {};
    const { id: plcId, name: placementName } = placement ?? {};

    const directKey = `${rhId}-${plcId}`;
    if (placementsById[directKey]) {
        return directKey;
    }

    const byNameMatches = findFilterPlacementsByRhAndName(
        placementName,
        rhName,
        placementsById,
        rightsholdersById,
    );

    // Refuse to guess when multiple placements share the same RH + name —
    // a wrong id would change reportQuery numbers, not just a label.
    if (byNameMatches.length !== 1 || byNameMatches[0]?.id == null) {
        if (byNameMatches.length > 1) {
            return null;
        }

        return directKey;
    }

    return String(byNameMatches[0].id);
};

const parseFilterPlacementId = (filterPlacementId?: string | null) => {
    if (!filterPlacementId) {
        return {
            rightsholderId: undefined,
            placementId: undefined,
        };
    }

    const [rightsholderId, placementId] = String(filterPlacementId).split('-');

    if (!rightsholderId || !placementId) {
        return {
            rightsholderId: undefined,
            placementId: undefined,
        };
    }

    return {
        rightsholderId: Number(rightsholderId),
        placementId: Number(placementId),
    };
};

const findBroadcastRspByRouteParams = (
    broadcastRsps: BroadcastRsp[] | undefined,
    {
        rightsholderId,
        sponsorId,
        placementId,
    }: {
        rightsholderId?: string;
        sponsorId?: string;
        placementId?: string;
    },
) => {
    if (
        !broadcastRsps?.length ||
        !rightsholderId ||
        !sponsorId ||
        !placementId
    ) {
        return undefined;
    }

    return broadcastRsps.find(
        rsp =>
            String(rsp.rightsholder.id) === rightsholderId &&
            String(rsp.sponsor.id) === sponsorId &&
            String(rsp.placement.id) === placementId,
    );
};

const getModifiedBroadcast = (filters, broadcasts, id) => {
    if (!broadcasts) return null;

    const currentBroadcast = broadcasts.byId[id];

    if (!currentBroadcast) return null;

    const { broadcastRsps } = currentBroadcast;

    const plcById = filters.placements.list;
    const rhsById = filters.rightsholders?.list ?? {};

    if (!isEmpty(plcById)) {
        const modifiedRsps = broadcastRsps.map(br => {
            const customPlacementName = resolvePlacementName(
                br,
                plcById,
                rhsById,
            );

            return {
                ...br,
                placement: {
                    ...br.placement,
                    name: customPlacementName,
                },
            };
        });

        return {
            ...currentBroadcast,
            broadcastRsps: modifiedRsps,
        };
    }

    return currentBroadcast;
};

const getFiltersToSave = filters => {
    const {
        insightsGroupBy,
        sort,
        order,
        placementTypes,
        sponsors,
        rightsholders,
        placements,
        broadcastNetworks,
        startDate,
        endDate,
        compareDates,
    } = filters;

    const selectedFilters = filter => setSelected(filter);
    const start = parseStartDate(startDate);
    const end = parseEndDate(endDate);

    return {
        startDate: start,
        endDate: end,
        rightsholders: getSelected(selectedFilters(rightsholders)),
        rightsholdersLeaguePairs: {},
        sponsors: getSelected(selectedFilters(sponsors)),
        placements: getSelectedPlacements(selectedFilters(placements)),
        placementTypes: getSelected(selectedFilters(placementTypes)),
        broadcastNetworks: getSelected(selectedFilters(broadcastNetworks)),
        platformPostTypePairs: [],
        accOwnTypes: [],
        mediaTypes: [],
        collections: [],
        hashtags: [],
        captions: [],
        accountUsernames: [],
        audiencePlatforms: [],
        networks: [],
        mvpScore: 0,
        broadcastNames: [],
        broadcastOptions: [],
        countries: [],
        ageBrackets: [],
        genders: [],
        insightsGroupBy,
        sort,
        order,
        compareDates,
    };
};

const hasSelectedDemographicFilters = filters =>
    (filters?.ageBrackets?.length ?? 0) > 0 ||
    (filters?.genders?.length ?? 0) > 0;

/**
 * Age Bracket / Gender only apply to the Demographics tab, so they are
 * dropped when the user leaves it. Every other filter (global and page
 * specific) is carried over untouched, in the shape `saveFilters` expects.
 */
const getFiltersWithClearedDemographics = filters => {
    const selectedOf = filter => filter?.selected ?? [];

    return {
        startDate: filters.startDate,
        endDate: filters.endDate,
        compareDates: filters.compareDates,
        rightsholders: selectedOf(filters.rightsholders),
        rightsholdersLeaguePairs: filters.rightsholdersLeaguePairs ?? {},
        sponsors: selectedOf(filters.sponsors),
        placements: selectedOf(filters.placements),
        placementTypes: selectedOf(filters.placementTypes),
        accOwnTypes: selectedOf(filters.accOwnTypes),
        mediaTypes: selectedOf(filters.mediaTypes),
        platformPostTypePairs: selectedOf(filters.platformPostTypePairs),
        audiencePlatforms: selectedOf(filters.audiencePlatforms),
        accountUsernames: filters.accountUsernames ?? [],
        networks: filters.networks ?? [],
        hashtags: filters.hashtags ?? [],
        captions: filters.captions ?? [],
        collections: selectedOf(filters.collections),
        mvpScore: filters.mvpScore,
        authorshipTypes: filters.authorshipTypes ?? [],
        collaboratingAccountUsernames:
            filters.collaboratingAccountUsernames ?? [],
        broadcastNames: filters.broadcastNames ?? [],
        broadcastOptions: filters.broadcastOptions ?? [],
        insightsGroupBy: filters.insightsGroupBy,
        sort: filters.sort,
        order: filters.order,
        countries: selectedOf(filters.countries),
        broadcastNetworks: selectedOf(filters.broadcastNetworks),
        dateRange: filters.dateRange,
        ageBrackets: [],
        genders: [],
    };
};

const findAllMentionsInContext = (context, mention) => {
    const contextSubstrings: { isMention: boolean; substring: string }[] = [];
    const safeContext = context ?? '';
    let startIndex = 0;

    if (!mention || mention.length === 0)
        return [
            {
                isMention: false,
                substring: safeContext,
            },
        ];

    let foundIndex: number = safeContext
        .toLowerCase()
        .indexOf(mention.toLowerCase(), startIndex);

    while (foundIndex !== -1) {
        contextSubstrings.push({
            isMention: false,
            substring: safeContext.slice(startIndex, foundIndex),
        });
        startIndex = foundIndex + mention.length; // Start search after the found substring
        contextSubstrings.push({
            isMention: true,
            substring: safeContext.slice(foundIndex, startIndex),
        });
        foundIndex = safeContext
            .toLowerCase()
            .indexOf(mention.toLowerCase(), startIndex);
    }

    if (safeContext.length > startIndex) {
        contextSubstrings.push({
            isMention: false,
            substring: safeContext.slice(startIndex),
        });
    }

    return contextSubstrings;
};

const getVerbalMentionPlacementIds = filters => {
    if (filters?.isLoading) return [];
    const selectedRightsHolders = filters?.rightsholders?.selected;

    const vmPlacements =
        (
            Object.values(filters?.placements?.list) as {
                id: string;
                name: string;
            }[]
        )
            .filter(
                p =>
                    p.name.indexOf('Verbal Mention') !== -1 &&
                    (selectedRightsHolders.length === 0 ||
                        selectedRightsHolders.includes(p.id.split('-')[0])),
            )
            .map(p => p.id) ?? [];

    return vmPlacements;
};

const sortKeysAndStringify = R.pipe(
    R.toPairs,
    R.sortBy(R.head),
    R.fromPairs,
    JSON.stringify,
);

export {
    getFiltersToSave,
    getModifiedBroadcast,
    resolvePlacementName,
    resolveFilterPlacementId,
    parseFilterPlacementId,
    findBroadcastRspByRouteParams,
    findAllMentionsInContext,
    getVerbalMentionPlacementIds,
    hasSelectedDemographicFilters,
    getFiltersWithClearedDemographics,
    sortKeysAndStringify,
    BroadcastTab,
    ExposureType,
};
